import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Episode, ProviderDetails, SeriesItem, VodItem } from "@hybrid-iptv/types";
import type { MatchResult } from "@hybrid-iptv/tmdb-client";
import { xtreamVodStreamId } from "@hybrid-iptv/iptv-parser";
import { api } from "../api/client.js";
import { usePlayerStore } from "../player/playerStore.js";
import { activeProviderSourceId, providerBody } from "../epg/api.js";
import { useDeviceSeededQuery } from "./useDeviceSeededQuery.js";
import { catalogCacheKey } from "../storage/catalogCache.js";
import { filterLockedItems, useParentalFilter } from "../settings/parentalStore.js";
import { usesTmdb } from "../discovery/rowSupplier.js";
import { createDetailsLine, type DetailsPriority } from "./providerDetailsLine.js";

export interface VodCatalog {
  vod: VodItem[];
  series: SeriesItem[];
}

/** Full VOD + series catalog for a source (Section 10), device-seeded so the
 * Movies/Series grids paint instantly on cold load and refresh in the
 * background with a write-through to IndexedDB. */
/** `ignoreParental` is for the parental settings picker ONLY: it must list the
 * categories it has hidden, and would otherwise filter them out of its own
 * chooser. Never pass it from a content surface. */
export function useVodCatalog(sourceId: string, enabled = true, ignoreParental = false) {
  // Same reasoning as useChannels: one filter here covers Movies, Series, Home
  // rails, My List and Search, because they all read the catalog through this.
  const parental = useParentalFilter();
  return useDeviceSeededQuery<VodCatalog>({
    queryKey: ["vod", sourceId],
    queryFn: () => api.post<VodCatalog>(`/api/sources/${sourceId}/vod`, providerBody()),
    cacheStore: "vod",
    cacheKey: catalogCacheKey("vod", sourceId),
    sizeOf: (catalog) => catalog.vod.length + catalog.series.length,
    enabled,
    staleTime: 1000 * 60 * 10,
    select: (catalog) =>
      ignoreParental
        ? catalog
        : {
            vod: filterLockedItems(catalog.vod, parental),
            series: filterLockedItems(catalog.series, parental),
          },
  });
}

/** Lazy season/episode details for one Xtream series (provider id in the URL). */
export function useSeriesDetails(sourceId: string, providerId: string | undefined) {
  return useQuery({
    queryKey: ["series-details", sourceId, providerId],
    // Sends the viewer's OWN source, like every other catalog call. It used to
    // send an empty body, which only worked because the server fell back to the
    // platform owner's provider account — so every user's episode lists were
    // fetched on that account. That fallback is closed (2026-09-14).
    queryFn: () => api.post<SeriesItem>(`/api/sources/${sourceId}/series/${providerId}`, providerBody()),
    enabled: !!providerId,
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * How a film's details are asked again after the panel turned the request away (the route
 * answers 502): four more times, about 2, 4, 8 and 16 s apart, so a busy moment passes (seen
 * 2026-10-11: The Uprising failed twice in a row while the catalog downloaded, then answered
 * in full). Home's hero and the film's page share these with the cache key.
 */
export const PROVIDER_DETAILS_RETRY = {
  retry: 4,
  retryDelay: (attempt: number) => Math.min(2_000 * 2 ** attempt, 20_000),
};

/**
 * The details line (providerDetailsLine.ts): requests go to the panel one at a time, a
 * viewer's ahead of Home's, and answers are kept between launches.
 */
const detailsLine = createDetailsLine({
  ask: (sourceId, streamId) =>
    api.post<ProviderDetails>(`/api/sources/${encodeURIComponent(sourceId)}/movie/${streamId}`, providerBody()),
});

/**
 * One film's details from the panel. Panels limit how many requests one account has open at
 * once: Home's hero asked about eight films together and most came back without their
 * details (2026-10-11), as the catalog download already learned ("panels count concurrent
 * connections"). Home's hero asks in the "background"; a film's page asks "now" and goes
 * first (2026-10-12: a page opened behind the hero's eight waited through all of them).
 * Home's hero and a film's page share this and its cache key, so a film asked about once is
 * never asked twice, and not again for a week.
 */
export function fetchProviderMovieDetails(
  sourceId: string,
  streamId: string,
  priority: DetailsPriority = "now",
): Promise<ProviderDetails> {
  return detailsLine.fetch(sourceId, streamId, priority);
}

/**
 * A film's details from the provider's panel (`get_vod_info`, Section 26), asked
 * for when its title page opens: plot, cast, director, genre, length, backdrop
 * and trailer, with no TMDB call. Xtream films only (an M3U film has no panel to
 * ask); the answer is kept for the session. A request the panel turned away is an
 * error (the route answers 502), so the next opening asks again.
 */
export function useProviderMovieDetails(movie: VodItem | null | undefined) {
  const streamId = movie ? xtreamVodStreamId(movie.streamUrl) : undefined;
  const sourceId = movie?.sourceId ?? "";
  // The banner may already have this film waiting in the background: a viewer is here now.
  useEffect(() => {
    if (streamId && sourceId) detailsLine.promote(sourceId, streamId);
  }, [sourceId, streamId]);
  return useQuery({
    queryKey: ["movie-details", sourceId, streamId],
    queryFn: () => fetchProviderMovieDetails(sourceId, streamId!),
    enabled: Boolean(streamId && sourceId),
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
    ...PROVIDER_DETAILS_RETRY,
  });
}

interface MatchResponse {
  rawTitle: string;
  mediaType: "movie" | "tv";
  match: MatchResult | null;
}

/**
 * On-demand TMDB match for a raw provider title. When the provider already
 * ships a TMDB id (Xtream `tmdb` field), that id wins — no network match
 * call — with a synthetic result so consumers read the same shape either
 * way. The fuzzy path is cached 7 days.
 */
function useTmdbMatch(media: "movie" | "tv", rawTitle: string, providerTmdbId?: number, enabled = true) {
  const hasProviderId = Number.isInteger(providerTmdbId) && (providerTmdbId ?? 0) > 0;
  const query = useQuery({
    queryKey: ["tmdb-match", media, hasProviderId ? `prov:${providerTmdbId}` : rawTitle],
    queryFn: () => api.post<MatchResponse>("/api/tmdb/match", { mediaType: media, rawTitle }),
    // Open (Section 26) has no fuzzy match: the provider's id, or none.
    enabled: enabled && usesTmdb() && !hasProviderId && rawTitle.trim().length > 0,
    staleTime: 1000 * 60 * 60 * 24 * 7,
  });
  const match: MatchResult | null = hasProviderId
    ? { tmdbId: providerTmdbId as number, confidence: 1, summary: { id: providerTmdbId as number, title: rawTitle } }
    : query.data?.match ?? null;
  return { ...query, data: { rawTitle, mediaType: media, match } };
}

export function useMovieMatch(rawTitle: string, providerTmdbId?: number, enabled = true) {
  return useTmdbMatch("movie", rawTitle, providerTmdbId, enabled);
}

/**
 * TMDB image CDN URL for a poster/backdrop path, or undefined when the path is
 * absent — so callers can `??` straight through to their own fallback.
 */
export function tmdbImageUrl(
  path: string | null | undefined,
  size: "w185" | "w342" | "w500" | "w780",
): string | undefined {
  return path ? `https://image.tmdb.org/t/p/${size}${path}` : undefined;
}

/** Full TMDB details (synopsis, genres, videos) for a detail overlay. */
export function useTmdbDetails(media: "movie" | "tv", tmdbId: number | undefined) {
  return useQuery({
    queryKey: ["tmdb-details", media, tmdbId],
    queryFn: () => api.post<TmdbDetailsShape>("/api/tmdb/details", { mediaType: media, id: tmdbId }),
    // Open (Section 26): title pages take the provider's details (titleFields.ts).
    enabled: usesTmdb() && Number.isInteger(tmdbId) && (tmdbId ?? 0) > 0,
    staleTime: 1000 * 60 * 60 * 24,
  });
}

export interface TmdbDetailsShape {
  id: number;
  title: string;
  overview?: string;
  poster_path?: string | null;
  backdrop_path?: string | null;
  release_date?: string | null;
  vote_average?: number;
  /** Minutes. Used as the seek-bar length for transcoded VOD, which has no duration. */
  runtime?: number;
  genres?: Array<{ id: number; name: string }>;
  credits?: {
    cast?: Array<{ id: number; name: string; character?: string; profile_path?: string | null }>;
  };
  recommendations?: { results?: Array<{ id: number; title: string; poster_path?: string | null; release_date?: string | null; vote_average?: number }> };
  similar?: { results?: Array<{ id: number; title: string; poster_path?: string | null; release_date?: string | null; vote_average?: number }> };
  videos?: { results?: Array<{ key: string; name: string; site?: string; type?: string }> };
}

/**
 * Trailer URL for a title. A provider-supplied trailer URL (Xtream `trailer` /
 * `youtube_trailer` field) wins — it is exact and needs no TMDB call. Falls
 * back to TMDB videos: prefers an official "Trailer", then a "Teaser".
 */
export function firstTrailerUrl(details: TmdbDetailsShape | undefined, providerTrailerUrl?: string): string | undefined {
  if (providerTrailerUrl) return providerTrailerUrl;
  const videos = (details?.videos?.results ?? []).filter((v) => v.site === "YouTube" && v.key);
  const trailer = videos.find((v) => v.type?.toLowerCase() === "trailer");
  const teaser = videos.find((v) => v.type?.toLowerCase() === "teaser");
  const video = trailer ?? teaser;
  return video ? `https://www.youtube.com/watch?v=${video.key}` : undefined;
}

/**
 * Convert a YouTube watch URL to an embeddable URL for in-app playback.
 * Uses the privacy-enhanced youtube-nocookie host so the app never leaks the
 * viewer's IP to Google just by rendering a poster.
 *
 * Options tune the embed: the fullscreen trailer autoplays with controls and
 * JS-API access (for remote media keys), while the home hero background runs
 * muted, looping, control-free, and click-transparent.
 */
export function youtubeEmbedUrl(
  trailerUrl: string | undefined,
  options: { autoplay?: boolean; mute?: boolean; controls?: boolean; loop?: boolean } = {},
): string | undefined {
  if (!trailerUrl) return undefined;
  const key = trailerUrl.match(/[?&]v=([^&]+)/)?.[1] ?? trailerUrl.match(/youtu\.be\/([^/?]+)/)?.[1];
  if (!key) return undefined;
  const params = new URLSearchParams({
    rel: "0",
    enablejsapi: "1",
    ...(options.autoplay !== false ? { autoplay: "1" } : {}),
    ...(options.mute ? { mute: "1" } : {}),
    ...(options.controls === false ? { controls: "0" } : {}),
    ...(options.loop ? { loop: "1", playlist: key } : {}),
  });
  return `https://www.youtube-nocookie.com/embed/${key}?${params.toString()}`;
}

export function useTvMatch(rawTitle: string, providerTmdbId?: number, enabled = true) {
  return useTmdbMatch("tv", rawTitle, providerTmdbId, enabled);
}

/**
 * Map TMDB id → catalog movie, built directly from the provider-supplied
 * `tmdbId` field (98% coverage on typical Xtream catalogs). No fuzzy
 * matching and no per-title network calls: a title missing from the map is
 * not in the playlist, period.
 */
export function useCatalogMovieMatches(catalog: VodItem[] | undefined) {
  return useMemo(() => {
    const byTmdbId = new Map<number, VodItem>();
    for (const item of catalog ?? []) {
      if (item.tmdbId && Number.isInteger(item.tmdbId) && item.tmdbId > 0 && !byTmdbId.has(item.tmdbId)) {
        byTmdbId.set(item.tmdbId, item);
      }
    }
    return byTmdbId;
  }, [catalog]);
}

/**
 * Resolve a title's TMDB runtime and attach it to the CURRENTLY PLAYING
 * source as `durationSeconds`. Search/Home clicks don't have the runtime
 * up-front (unlike the Movies detail flow), so it's fetched asynchronously
 * after playback starts — the seek bar grows to span the whole title when
 * TMDB responds instead of staying capped at the transcode frontier.
 *
 * Guarded so a late response never patches a different source: only the
 * source with the same title, still transcoded (direct VOD reports its own
 * duration), and without a duration already known gets patched.
 */
export function patchTranscodedRuntime(tmdbId: number | undefined, title: string, streamUrl?: string): void {
  void runtimeMinutesFor(tmdbId, streamUrl)
    .then((minutes) => {
      if (typeof minutes !== "number" || minutes <= 0) return;
      const player = usePlayerStore.getState().player;
      const current = player?.getState().source;
      if (!player || !current) return;
      if (current.title !== title || current.transcoded !== true || current.durationSeconds) return;
      player.updateDuration(minutes * 60);
    })
    .catch(() => undefined);
}

/** A film's length in minutes: TMDB's, or under Open (Section 26) the provider's (get_vod_info). */
async function runtimeMinutesFor(tmdbId: number | undefined, streamUrl: string | undefined): Promise<number | undefined> {
  if (usesTmdb()) {
    if (!(tmdbId && Number.isInteger(tmdbId) && tmdbId > 0)) return undefined;
    return (await api.post<TmdbDetailsShape>("/api/tmdb/details", { mediaType: "movie", id: tmdbId }))?.runtime;
  }
  const streamId = streamUrl ? xtreamVodStreamId(streamUrl) : undefined;
  const sourceId = activeProviderSourceId();
  if (!streamId || !sourceId) return undefined;
  return (await api.post<ProviderDetails>(`/api/sources/${encodeURIComponent(sourceId)}/movie/${streamId}`, providerBody()))?.runtimeMinutes;
}

/** Start playback of a VOD item or episode through the shared player. The
 * film's length lands asynchronously (see patchTranscodedRuntime: TMDB's, or
 * the panel's under Open) so the seek bar spans the whole title even from
 * entry points without details loaded. */
export function playVodItem(url: string, title: string, artworkUrl?: string, tmdbId?: number): void {
  void usePlayerStore.getState().playSource({ url, type: "vod", title, artworkUrl });
  patchTranscodedRuntime(tmdbId, title, url);
}

export function episodeKey(episode: Episode): string {
  return `ep:${episode.seriesId}:s${episode.seasonNumber}e${episode.episodeNumber}`;
}
